import { Component, Input, ViewChild } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { AgGridAngular } from "ag-grid-angular";
import {
    AgGridEvent,
    ColDef,
    GridApi,
    GridOptions,
    IAggFuncParams,
    RowNode,
    ValueFormatterParams,
    ValueGetterParams,
} from "ag-grid-community";
import { Subscription } from "rxjs";
import { AgentStateDetailComponent } from "src/app/dashboard/modals/agent-state-detail/agent-state-detail.component";
import { SuggestCoachingModalComponent } from "src/app/dashboard/modals/suggest-coaching-modal/suggest-coaching-modal.component";
import { TasksModalComponent } from "src/app/dashboard/modals/tasks-modal/tasks-modal.component";
import { NavigationService } from "src/app/navigation/navigation.service";
import { ObjectivesDetailsService } from "src/app/reusables/objectives-details/objectives-details.service";
import { AgContextMenuService } from "src/app/reusables/services/ag-context-menu.service";
import { AgGridHelperService } from "src/app/reusables/services/ag-grid-helper.service";
import { CvConfigurationService } from "src/app/reusables/services/cv-configuration.service";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import SimpleContextMenuRenderer from "src/shared/ag-grid-components/simple-context-menu";
import { ErrorCode } from "src/shared/models/error-code.model";
import { NumericType } from "src/shared/models/metric";
import ModuleDataGroupItem from "src/shared/models/module-data-group-item";
import { ModuleManager } from "src/shared/models/module-manager";
import {
    IModuleManagerMenuItem,
    ModuleManagerMenuItemType,
} from "src/shared/models/module-manager/module-manager-menu-item.model";
import { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import { CurrentAgentStatesService } from "../../current-agent-states/current-agent-states.service";
import { ExpandedSummaryData } from "../expanded-summary.model";
import { ExpandedSummaryService } from "../expanded-summary.service";

@Component({
    selector: "cv-expanded-summary-pm",
    templateUrl: "./expanded-summary-pm.component.html",
    styleUrl: "./expanded-summary-pm.component.scss",
})
export class ExpandedSummaryPmComponent {
    @ViewChild("expandedSummaryWidgetGrid") agGrid: AgGridAngular;
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    modalRef: NgbModalRef = null;

    public data: ExpandedSummaryData = null; // A copy of data returned by loadData will be stored here.
    public isNoMetric = false;
    public isDateRangeTooWideIntraday = false;
    public isNoData = false;
    public filterValue = ""; // This variable contains the contents of the search bar.
    public showFilter = false;
    public showTotal = true;
    public gridOptions: GridOptions = null; // Ag-grid configuration:
    public isDataFirstLoaded = false;
    public isTooMuchData = false;

    //For cxone search placeholder localization
    searchPlaceholder = "";

    private gridState: any = null; // This object holds previous state for ag-grid, and saves it to
    private isDataLoading = false; // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
    private isFirstLoad = true; // This variable is used for logging.  ModuleResponsiveController should accept isAutoRefresh and isFirstLoad variables.
    private stateSaveTimer: number;
    private setupGridTimer: number;
    private tableDisplayItems = [];

    private abortLoad: () => void = null;
    private subscriptions: Subscription[] = [];

    isAgentStateDetailModalOpen = false;

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService, // Used to pull translations.  Most strings should be localized in the markup aspx file, but there are some strings (like AgGrid) that need to be localized in the controller code.
        private expandedSummaryService: ExpandedSummaryService,
        public config: CvConfigurationService,
        private agentStateService: CurrentAgentStatesService,
        private ngbModal: NgbModal,
        private navigationService: NavigationService,
        private agContextMenuService: AgContextMenuService,
        private objectivesDetailsService: ObjectivesDetailsService
    ) {}

    private get isResizeWaiting() {
        return this.moduleManager?.container.isResizeWaiting;
    }

    // Called by Angular when the search bar's value has changed.
    public applyFilter() {
        if (this.agGrid?.api) {
            // notify ag-grid that the filter changed
            // this.agGrid?.api.onFilterChanged(); // Used with the External Filter.
            const quickFilterText =
                this.filterValue != null ? this.filterValue : "";
            this.agGrid?.api.setGridOption("quickFilterText", quickFilterText);
        }
    }

    /**
     * Called when the clear button on the filter box is clicked.  Clears the filter
     */
    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    async loadLocalization() {
        const localizationPromises = [
            this.cvLocalizationService.GetSection("Template_Module"),
            this.cvLocalizationService.GetSection("Common"),
            this.cvLocalizationService.GetSection("Objectives"),
            this.cvLocalizationService.GetSection("Module_Menu"),
        ];
        await Promise.all(localizationPromises);
        this.searchPlaceholder = this.cvLocalizationService.getCachedText(
            "Common",
            "Search",
            "Search"
        );
    }

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        this.loadLocalization();

        this.tableDisplayItems = [
            // items for the display menu that uses both radio buttons and checkboxes
            {
                displayName: this.cvLocalizationService.getCachedText(
                    "Module_Menu",
                    "Show Totals",
                    "Show Totals"
                ),
                inputType: "radio",
                value: true,
                checked: this.showTotal,
            },
            {
                displayName: this.cvLocalizationService.getCachedText(
                    "Common",
                    "None",
                    "None"
                ),
                inputType: "radio",
                value: false,
                checked: this.showTotal,
            },
        ];

        // Module Configuration:
        this.moduleManager.setOptions({
            enableWidthClass: false, // enabling this will add mediaquery-like classes to 'moduleResponsiveContainer'
            widgetMode: false, // enabling this disables the module header and moves the menu to the body of the module
            onSettingsApplied: () => {
                this.loadData(false, true);
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: (isAutoRefresh) => {
                if (!isAutoRefresh) this.moduleManager.showLoader = true;
                return this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading, // determines whether a module's refresh menu item is clickable.  Can't refresh when there's already one in progress.
            onExport: (format) =>
                this.expandedSummaryService.getExport(this.moduleKey, format),
        });

        this.setupGrid();
        // Bound values ("@Input()" above) are only valid in and after this function.
        // Show the loading spinner.  This will be unset in the loadData() function:
        this.moduleManager.showLoader = true;

        // Example header item.  Usually used for the magnifying glass for the search bar.
        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        // #region States and Settings
        // States - used for saving values that are not set in settings
        // example: what column is sorted on a table
        // To save a state: call this.moduleManager.saveState() and all state values will be saved
        this.moduleManager.addState({
            name: "gridState",
            getStateData: () => this.gridState,
            onStateLoaded: (stateData) => {
                if (!stateData) return;

                this.gridState = stateData;
                if (this.agGrid?.api)
                    this.agGridHelperService.loadGridState(
                        this.agGrid?.api,
                        stateData
                    );
            },
            // if autoload is true, this state will start loading immediately
            // and call onStateLoaded() when done
            // to load manually set to false and call this.moduleManager.loadState("sortedColumn");
            autoLoad: true,
        });

        // This stores the state of the Totals row.  The user can check/uncheck it in the module menu.  This value is persisted.
        this.moduleManager.addState({
            name: "footerVisible",
            getStateData: () => this.showTotal,
            onStateLoaded: (state: boolean) => {
                this.showTotal = state;
                this.setTotalsRowData();
                this.tableDisplayItems
                    .filter((i) => i.inputType === "radio")
                    .forEach((item) => {
                        item.checked = !!state;
                    });
            },
            autoLoad: true,
        });

        // Settings - used for adding custom settings to the settings dialog
        // requires a [Template]Settings directive (see _TemplateSettings.js/aspx)

        this.moduleManager.customSettingsAdded = true;

        // assures menu items are set on the first load
        this.resetMenuItems();
    }

    ngOnDestroy(): void {
        // Called once, before the instance is destroyed.
        this.modalRef = null;
        this.subscriptions.forEach((sub) => {
            sub.unsubscribe();
        });
    }

    /** Opens the Agent State Detail modal from the Context Menu on Groups column */
    openAgentStateDetail(moduleKey: string, groupID: number, selectedRow: any) {
        if (this.isAgentStateDetailModalOpen) return;
        this.isAgentStateDetailModalOpen = true;
        this.navigationService.showOverlay = true;

        this.modalRef = this.ngbModal.open(AgentStateDetailComponent, {
            size: "xl",
            centered: true,
            scrollable: true,
            modalDialogClass: null,
        });
        this.modalRef.result.finally(() => {
            this.navigationService.showOverlay = false;
            this.isAgentStateDetailModalOpen = false;
        });

        const component = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey =
            this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openCreateTaskModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(TasksModalComponent, {
            size: "xl",
            centered: true,
            scrollable: true,
            modalDialogClass: null,
        });
        this.modalRef.result.finally(
            () => (this.navigationService.showOverlay = false)
        );
        const component: TasksModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey =
            this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openSuggestCoachingModal(
        moduleKey: string,
        groupID: number,
        selectedRow: any
    ) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(SuggestCoachingModalComponent, {
            size: "md",
            centered: true,
            scrollable: true,
            modalDialogClass: null,
        });
        this.modalRef.result.finally(
            () => (this.navigationService.showOverlay = false)
        );
        const component: SuggestCoachingModalComponent =
            this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey =
            this.moduleManager.container.dashboardManager.dashboardKey;
    }

    /**
     * Sets up the grid options.  Should be called after localizations are loaded, because the column headers are localized.
     */
    private setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions(
            {
                defaultColDef: {
                    minWidth: this.agGridHelperService.defaultMinWidth,
                },
                context: {
                    moduleManager: this.moduleManager,
                    getMetric: (params) =>
                        params.node.data.MetricDatas[params.colDef.colId],
                },
                groupDefaultExpanded: 1,
                autoGroupColumnDef: {
                    cellRendererParams: {
                        suppressCount: true, // This suppresses the row group header's count of the number of rows in the group.
                    },
                },
                suppressAggFuncInHeader: true, // If the metric is called "Calls", AggFuncInHeader displays it as "func(Call)". Suppress this function string.
                components: {
                    groupRenderer:
                        this.agGridHelperService.GroupDrillDownCellRenderer,
                    groupHeaderComponent:
                        this.agGridHelperService.GroupHeaderComp,
                    metricMenuRenderer: SimpleContextMenuRenderer,
                },
                onFirstDataRendered: (e) => {
                    if (this.gridState == null) {
                        this.sizeColumns(e);
                    }
                },
                onGridReady: (e) => {
                    if (this.data?.Error === ErrorCode.NoError) {
                        this.queueUpdateGrid();
                        this.sizeColumns(e);
                    }
                    this.applyFilter();
                },
                // This function defines which field from the row data is the "primary key".  It's used for delta updates between different setRowData() calls.
                // This "ID" property is the same as the above columnDef.  Usually it'll be a group id.  If there is no "primary key" for the data you're trying to display.
                getRowId: ({ data: rowData }) =>
                    `${rowData.PrimaryGroup.groupID}-${rowData.SecondaryGroup.groupID}`,

                // The filter can also be set with the setQuickFilter() api call on the grid.
                // isExternalFilterPresent: () => this.filterValue.length > 0, // External Filter refers to our search bar.  This function returns true when $ctrl.filterValue has content, false when otherwise.  False causes ag-grid to show all row values.
                // doesExternalFilterPass: node => this.agGridHelperService.doesExternalFilterPass(node, this.filterValue), // This function actually uses the value of the search bar, and it compares it to the data of the row ag-grid is passing in (node).  Use agGridHelperFactory's unless you have good reason.
                onSortChanged: () => this.queueStateSave(),
                onColumnMoved: () => this.queueStateSave(),
                onColumnPinned: () => this.queueStateSave(),
                onColumnVisible: (e) => {
                    this.sizeColumns(e);
                    this.queueStateSave();
                },
                onGridSizeChanged: (e) => {
                    if (this.isResizeWaiting) {
                        this.sizeColumns(e);
                        this.queueStateSave();
                    }
                },
                onColumnResized: () => this.queueStateSave(),
                getContextMenuItems: (params) =>
                    this.getContextMenuItem(params),
            },
            {
                enableCopy: true,
                enableChart: false, // Ag-Grid chart does not work with grouped rows
            }
        );

        // Wire context menus
        this.subscriptions.push(
            this.agContextMenuService.agentDetailsClicked.subscribe(
                async (params) => {
                    // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
                    if (params.moduleKey !== this.moduleManager.getModuleKey())
                        return;
                    const response =
                        await this.agentStateService.loadCurrentAgentStatesByGroupID(
                            params.moduleKey,
                            params.groupData.groupID
                        );
                    this.openAgentStateDetail(
                        params.moduleKey,
                        params.groupData.groupID,
                        response
                    );
                }
            )
        );

        this.subscriptions.push(
            this.agContextMenuService.createTaskClicked.subscribe(
                async (params) => {
                    // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
                    if (
                        params.moduleKey !== this.moduleManager.getModuleKey()
                    ) {
                        return;
                    }
                    this.openCreateTaskModal(
                        params.moduleKey,
                        params.groupData.groupID,
                        params.groupData
                    );
                }
            )
        );

        this.subscriptions.push(
            this.agContextMenuService.suggestCoachingClicked.subscribe(
                async (params) => {
                    if (
                        params.moduleKey !== this.moduleManager.getModuleKey()
                    ) {
                        return;
                    }
                    this.openSuggestCoachingModal(
                        params.moduleKey,
                        params.groupData.groupID,
                        params.groupData
                    );
                }
            )
        );
    }

    // Grouped View:
    // Group Header         | Metric 1 Abv Header     | Metric 2 Abv Header     | ...
    // ---------------------+-------------------------+-------------------------+----
    // > Primary Group 1    | Metric-Interval Total 1 | Metric-Interval Total 2 | ...
    // v Primary Group 2    | Metric-Interval Total 1 | Metric-Interval Total 2 | ...
    //    Secondary Group 1 | Group-Metric Value 1    | Group-Metric Value 2    | ...
    //    Secondary Group 2 | Group-Metric Value 1    | Group-Metric Value 2    | ...
    // > Primary Group 3    | Metric-Interval Total 1 | Metric-Interval Total 2 | ...
    private updateColumnDefs() {
        if (!this.agGrid?.api) return;
        this.agGrid.api.setGridOption("columnDefs", []);
        const columnDefs = [];
        if (this.data.DataRow.length !== 0) {
            columnDefs.push({
                field: "PrimaryGroup",
                colId: "PrimaryGroup",
                headerName: this.data.DataRow[0].PrimaryTypeName,
                rowGroup: true,
                hide: true,
                valueGetter: (params: ValueGetterParams) =>
                    params?.node?.data?.PrimaryGroup?.groupName,
                valueFormatter: (params: ValueFormatterParams) =>
                    params?.node?.data?.PrimaryGroup?.groupName,
                aggFunc: () => "",
            });

            // This call is deprecated and will likely be removed in the future.
            // Future solution will probably have to be to set this in the setupGrid function and then use a headerCellRenderer to set the name dynamically
            this.agGrid?.api.setAutoGroupColumnDef({
                headerName: this.data.DataRow[0].PrimaryTypeName,
                pinned: "left",
                minWidth: 100,
                resizable: true,
                cellRendererParams: {
                    suppressCount: true, // This suppresses the row group header's count of the number of rows in the group.
                },
            } as ColDef);
            columnDefs.push({
                field: "SecondaryGroup",
                colId: "SecondaryGroup",
                headerName: this.data.DataRow[0].SecondaryTypeName,
                minWidth: 100,
                resizable: true,
                valueGetter: (params: ValueGetterParams) =>
                    params?.node?.data?.SecondaryGroup?.groupName,
                valueFormatter: (params: ValueFormatterParams) =>
                    params?.node?.data?.SecondaryGroup?.groupName,
                cellRendererSelector: (params) => {
                    // TODO: Ag Grid Update 30.0.0 - pinnedRowCellRenderer is deprecated. 'Use cellRendererSelector instead'. This implementation may not work though
                    if (params.node.rowPinned) {
                        return {
                            component: () =>
                                !this.showTotal
                                    ? this.cvLocalizationService.getCachedText(
                                          "Interval_Module",
                                          "Averages",
                                          "Averages"
                                      )
                                    : this.cvLocalizationService.getCachedText(
                                          "Interval_Module",
                                          "Totals",
                                          "Totals"
                                      ),
                            params: {
                                suppressCount: true,
                            },
                        };
                    }
                },
                cellRendererParams: {
                    suppressCount: true,
                    innerRenderer:
                        this.agGridHelperService.GroupDrillDownCellRenderer,
                },
                aggFunc: (params: IAggFuncParams) =>
                    params?.values?.[0]?.groupName,
                filterParams: {
                    valueGetter: (node: RowNode) =>
                        (node?.data?.GroupData as ModuleDataGroupItem)
                            ?.groupName,
                },
            });

            for (let i = 0; i < this.data.Metrics.length; i++) {
                const ugColDef = this.data.Metrics[i];

                let agColDef: ColDef;
                if (ugColDef.Abbreviation.startsWith("UAttribute")) {
                    const isRenamed =
                        ugColDef.MetricCustomName &&
                        ugColDef.MetricCustomName !== "";
                    agColDef = {
                        headerName: isRenamed
                            ? ugColDef.MetricCustomName + "*"
                            : ugColDef.Abbreviation,
                        headerTooltip: isRenamed
                            ? this.cvLocalizationService.getCachedText(
                                  "Common",
                                  "Original_Metric",
                                  "Original Metric Name"
                              ) +
                              ": " +
                              "\n" +
                              ugColDef.Abbreviation
                            : null,
                        field: ugColDef.Abbreviation,
                        type: "ellipsesColumn",
                        minWidth: 50,
                        resizable: true,
                    };
                } else {
                    const isRenamed =
                        ugColDef.MetricCustomName &&
                        ugColDef.MetricCustomName !== "";
                    agColDef = {
                        headerName: isRenamed
                            ? ugColDef.MetricCustomName + "*"
                            : ugColDef.Abbreviation,
                        headerTooltip: isRenamed
                            ? this.cvLocalizationService.getCachedText(
                                  "Common",
                                  "Original_Metric",
                                  "Original Metric Name"
                              ) +
                              ": " +
                              "\n" +
                              ugColDef.Abbreviation
                            : null,
                        field: ugColDef.Abbreviation,
                        colId: `${this.data.Metrics[i].MetricID}`,
                        minWidth: 50,
                        resizable: true,
                        valueGetter: (node: ValueGetterParams) =>
                            node.data?.MetricDatas[
                                this.data?.Metrics[i].MetricID
                            ] ?? 0,
                        aggFunc: (params: IAggFuncParams) => {
                            if (!params.rowNode?.allLeafChildren?.[0]?.id)
                                return null;

                            const groupId =
                                params.rowNode.allLeafChildren[0].id.split(
                                    "-"
                                )[0];
                            return (
                                this.data.MetricGroupTotals?.[
                                    `${groupId}-${this.data.Metrics[i].MetricID}`
                                ] ?? null
                            );
                        },
                        filterParams: {
                            valueGetter: (node: RowNode) =>
                                node.data.MetricDatas[
                                    this.data.Metrics[i].MetricID
                                ] ?? 0,
                        },
                    };
                }

                // add the metric specific functions for handling MetricDisplayValue and MetricValue as well as performance objectives
                if (ugColDef.MetricID > 0) {
                    agColDef.type = "metricColumn";
                    agColDef.cellRenderer = "metricMenuRenderer"
                    agColDef.cellRendererParams = {
                        isExpandedSummary: true,
                    };
                }

                columnDefs.push(agColDef);
            }
        }

        this.agGrid?.api.setGridOption("columnDefs", []);
        this.agGrid?.api.setGridOption("columnDefs", columnDefs);

        if (this.gridState != null && this.agGrid?.api) {
            if (this.gridState.columns.find(c => c.name == "PrimaryGroup").rowGroup) {
                this.agGridHelperService.loadGridState(
                    this.agGrid?.api,
                    this.gridState
                );
            } else {
                this.queueStateSave();
            }
        }
    }

    // Called by the context menu renderer to get the context menu items
    private objectivesDetailsModalClicked(contextMenuParams: any) {
        let groupData;
        let value = angular.copy(contextMenuParams.value);

        if (contextMenuParams.node.group) {
            groupData =
                contextMenuParams.node.allLeafChildren[0].data.PrimaryGroup;
        } else {
            groupData = contextMenuParams.node.data.SecondaryGroup;
        }

        const metricMatch = this.data.Metrics.find(
            (m) => m.MetricID === value.MetricID
        );
        if (metricMatch.NumericType === NumericType.Percent)
            value.MetricValue *= 100;

        this.objectivesDetailsService.openObjectivesDetailsModal(
            value,
            groupData,
            contextMenuParams.column.colDef.headerName
        );
    }

    private getContextMenuItem(params) {
        if (typeof params.value === "string") {
            return this.agGridHelperService.getContextMenuItems(params);
        }
        return [
            {
                name: this.cvLocalizationService.getCachedText(
                    "Objectives",
                    "Objectives_Details",
                    "Objectives Details"
                ),
                action: () => this.objectivesDetailsModalClicked(params),
                icon: `<svg class="icon-16" style="fill: #6a7480;">
                        <use href="../App_Themes/clearview-default/icons/dashboard/gauge-range.svg#gauge-range"></use>
                    </svg>`,
            },
        ];
    }

    /**
     * Shows and hides the search/filter bar
     */
    private toggleOpenSearch() {
        // called by moduleManager.addHeaderItem for search bar.
        if (this.showFilter) {
            this.showFilter = false;
            this.clearFilter();
        } else {
            this.showFilter = true;
        }
    }

    // Example loadData function:
    private async loadData(
        isAutoRefresh: boolean,
        abortPrevious: boolean = false
    ) {
        this.isNoData = false;
        this.isNoMetric = false;
        this.isTooMuchData = false;
        this.isDateRangeTooWideIntraday = false;

        if (this.isDataLoading)
            if (abortPrevious)
                this.abortLoad(); // If reload was triggered by saving settings abort previous request
            else return; // don't have multiple outgoing requests
        this.isDataLoading = true;

        const abort: Promise<void> = new Promise<void>(
            (res, rej) => (this.abortLoad = () => res())
        );
        let aborted = false;

        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
            // Clear grid data when loading to prevent showing stale data
            if (this.agGrid?.api) {
                this.agGrid.api.setGridOption("rowData", []);
                this.agGrid.api.setGridOption("pinnedBottomRowData", []);
            }
        }

        try {
            this.data = await this.expandedSummaryService.getData(
                this.moduleKey,
                isAutoRefresh,
                this.isFirstLoad,
                abort
            );
        } catch (err) {
            if (err instanceof DOMException && err.name === "AbortError")
                aborted = true;
            this.moduleManager.handleLoadError(err, isAutoRefresh);
        }

        this.isDataLoading = false;
        this.isFirstLoad = false;
        this.moduleManager.setAutoRefreshReady();

        if (this.data?.TooMuchData) {
            this.isTooMuchData = true;
            this.moduleManager.showLoader = false;
            return;
        }

        if (!aborted) {
            if (
                this.data?.Error === 0 &&
                (!this.data?.MetricTotals ||
                    !this.data?.Metrics ||
                    !this.data?.MetricTotals ||
                    !this.data?.DataRow)
            ) {
                this.isNoData = true;
                this.moduleManager.showLoader = false;
                return;
            } else if (this.data?.Error === 1) {
                this.isNoMetric = true;
                this.moduleManager.showLoader = false;
                return;
            } else if (this.data?.Error === 2) {
                this.isDateRangeTooWideIntraday = true;
                this.moduleManager.showLoader = false;
                return;
            } else if (!this.data?.DataRow?.length) {
                this.isNoData = true;
                this.moduleManager.showLoader = false;
                return;
            }
        }

        if (this.agGrid?.api && this.data?.DataRow?.length > 0) {
            this.queueUpdateGrid();
        }
    }

    // Called by some of ag-grid's events
    private sizeColumns(event: AgGridEvent) {
        if (!this.data?.DataRow?.length) {
            return;
        }

        // helper function to size columns
        if (event) this.agGridHelperService.sizeColumnsSmart(event);
        else
            this.agGridHelperService.sizeColumns(
                this.agGrid?.api,
                this.gridOptions
            );
    }

    /**
     * Saves the grid's state save, debounced to 500ms.
     */
    private queueStateSave() {
        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        // Create a timer to save the state 500ms from now.
        this.stateSaveTimer = window.setTimeout(() => {
            this.stateSaveTimer = null;
            if (this.agGrid?.api)
                this.gridState = this.agGridHelperService.getGridState(
                    this.agGrid?.api
                );
            this.moduleManager.saveState(); // This will save all moduleManager.addState definitions
        }, 500 /*ms*/);
    }

    private queueUpdateGrid() {
        if (!this.agGrid?.api) return;

        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.setupGridTimer) {
            clearTimeout(this.setupGridTimer);
            this.setupGridTimer = null;
        }

        // Create a timer to save the state 500ms from now.
        this.setupGridTimer = window.setTimeout(() => {
            this.setupGridTimer = null;
            this.updateColumnDefs();
            this.agGrid?.api.setGridOption("rowData", this.data.DataRow);
            this.setTotalsRowData();

            this.isDataFirstLoaded = true;
            this.moduleManager.container.showDateRange = true;
            this.moduleManager.showLoader = false;
        }, 500 /*ms*/);
    }

    /**
     * Resets the module menu items
     */
    resetMenuItems() {
        this.moduleManager.resetMenu();
        this.addTableMenuItems();
    }

    /**
     * Adds the table menu items
     */
    addTableMenuItems() {
        this.moduleManager.addMenuItem({
            name: "Table Display", // Menu items are localized automatically by Module Container.  Keys under section Module_Menu
            icon: "tableDisplay",
            getEnabled: () => !this.isNoData,
            type: ModuleManagerMenuItemType.RadioCheckMenu,
            getCheckItems: () => this.tableDisplayItems,
            getItemName: (i) => this.tableDisplayItems[i].displayName,
            getItemInputType: (i) => {
                return i <= this.tableDisplayItems.length - 1
                    ? this.tableDisplayItems[i]?.inputType
                    : "checkbox";
            },
            isItemChecked: (i) => this.tableDisplayItems[i].checked,
            onCheckClicked: (i) => {
                if (this.tableDisplayItems[i].inputType === "radio") {
                    this.showTotal = this.tableDisplayItems[i].value;
                    this.tableDisplayItems
                        .filter((i) => i.inputType === "radio")
                        .forEach((item) => {
                            item.checked = this.showTotal;
                        });
                    this.setTotalsRowData();
                } else {
                    this.tableDisplayItems[i].checked =
                        !this.tableDisplayItems[i].checked;
                }
                this.queueStateSave();
                this.moduleManager.requestPublishNeeded();
            },
            readOnlyEnabled: true,
        } as IModuleManagerMenuItem);
    }

    /**
     * If the user has enabled the totals row, this sets the data.
     * Otherwise it clears the data to hide the row.
     *
     * @param api Optional GridApi reference.
     */
    setTotalsRowData(api?: GridApi) {
        api = api || this.agGrid?.api;
        if (!api) return;

        if (this.showTotal && this.data?.MetricTotals)
            this.agGrid?.api.setGridOption("pinnedBottomRowData", [
                { MetricDatas: this.data.MetricTotals },
            ]);
        //will be implemented in the future
        //else if (this.showTotal && this.data?.MetricAverages)
        //    this.agGrid?.api.setGridOption('pinnedBottomRowData', [{ MetricDatas: this.data.MetricAverages }]);
        else this.agGrid?.api.setGridOption("pinnedBottomRowData", []);
    }
}
